/**
 * Axios-Client mit:
 *  - Auth-Interceptor (Access-Token aus Auth-Store)
 *  - Refresh-Loop (bei 401 einmal /auth/refresh probieren, Queue für parallele Calls)
 *  - Correlation-ID-Header für End-to-End-Tracing (V1-Lehre Nr. 8)
 */
import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";

import { useAuthStore } from "@/auth/store";

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Refresh-Cookie wird mitgeschickt
  headers: { "Content-Type": "application/json" },
});

// ── Correlation-ID + Access-Token ────────────────────────────
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  if (!config.headers.has("X-Correlation-ID")) {
    config.headers.set("X-Correlation-ID", crypto.randomUUID());
  }
  return config;
});

// ── 401 → einmal refreshen, dann queue abarbeiten ────────────
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  try {
    const response = await axios.post<{ access_token: string; expires_in: number }>(
      `${BASE_URL}/v1/auth/refresh`,
      null,
      { withCredentials: true },
    );
    const { access_token } = response.data;
    useAuthStore.getState().setAccessToken(access_token);
    return access_token;
  } catch {
    useAuthStore.getState().clear();
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    if (!original || error.response?.status !== 401 || original._retried) {
      return Promise.reject(error);
    }

    // /auth/refresh selbst darf nicht in den Refresh-Loop laufen
    if (original.url?.endsWith("/v1/auth/refresh")) {
      useAuthStore.getState().clear();
      return Promise.reject(error);
    }

    original._retried = true;
    refreshPromise ??= refreshAccessToken().finally(() => {
      refreshPromise = null;
    });

    const newToken = await refreshPromise;
    if (!newToken) {
      return Promise.reject(error);
    }

    original.headers.set("Authorization", `Bearer ${newToken}`);
    return api.request(original);
  },
);
